import { Typography, Tabs, Tab, FormControlLabel, Switch, Grid2 as Grid } from '@mui/material';
import Fuse from 'fuse.js';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActiveGuidesTable, PlannedGuidesTable, ExpiredGuidesTable, UnpublishedGuidesTable } from './GuideTable';
import { selectGuideFilter, showOnlyMine as setShowOnlyMine, showTab } from './filterSlice';
import { GET_GUIDES } from './guide.graphql';
import { SearchField } from '../components/SearchField';
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';

import { NewGuideButton } from './NewGuideDialog';
import { GetGuidesQuery } from '../__generated__/graphql';
import { Unpacked } from '../graphQLTypes/types';
import { useAuth } from 'oidc-react';
import { StyledPaper } from '../theme';
import { Box, Stack } from '@mui/system';

export type Guide = Unpacked<GetGuidesQuery['guides']>;

export function Overview() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const { showOnlyMine, tab } = useSelector(selectGuideFilter);
  const auth = useAuth();
  const userId = auth.userData?.profile.sub;
  const showOnlyMineFromParam = searchParams.get('mine') === '1';
  const { loading, data } = useQuery(GET_GUIDES, { fetchPolicy: `cache-and-network` });

  if (!showOnlyMine && showOnlyMineFromParam) dispatch(setShowOnlyMine(true));

  const filteredGuides = data?.guides ? filterGuides(data.guides, searchTerm, showOnlyMine ? userId : undefined) : [];

  return (
    <StyledPaper>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center">
          <Typography variant="h5">Anvisninger</Typography>
          <NewGuideButton />
        </Stack>
        <Stack>
          <SearchField filter={setSearchTerm} />
          <FormControlLabel
            control={<Switch name="showOnlyMine" onChange={(_, checked) => dispatch(setShowOnlyMine(checked))} checked={showOnlyMine} />}
            label="Mine"
          />
        </Stack>
        <Box>
          <Tabs value={tab} onChange={(_, val) => dispatch(showTab(val))}>
            <Tab label="Aktive" />
            <Tab label="Nye" />
            <Tab label="Tilbaketrukne" />
            <Tab label="Upubliserte  anvisninger" />
          </Tabs>
          {loading && !data && <LoadingSpinner />}
          {tab === 0 && <ActiveGuidesTable guides={filteredGuides} />}
          {tab === 1 && <PlannedGuidesTable guides={filteredGuides} />}
          {tab === 2 && <ExpiredGuidesTable guides={filteredGuides} />}
          {tab === 3 && <UnpublishedGuidesTable guides={filteredGuides} />}
        </Box>
      </Stack>
    </StyledPaper>
  );
}

function filterGuides(guides: Guide[], searchTerm: string, userId?: string): Guide[] {
  const filteredGuides = userId ? guides && guides.filter((x) => x && x.owner && x.owner.subjectId === userId) : guides;
  if (!searchTerm) return filteredGuides;

  let options = {
    shouldSort: false,
    threshold: 0,
    ignoreLocation: true,
    minMatchCharLength: 3,
    keys: ['docName', 'docNumber', 'docTitle', 'mainCategory.title', 'owner.name'],
  };

  const fuse = new Fuse(filteredGuides, options);
  return fuse.search(searchTerm).map((x) => x.item);
}
